<template>
  <MeteringPointCard :metering-point :show-title>
    <LoadingWrapper :error="loadingFailed" :loading="loading">
      <!-- Stat row -->
      <div v-if="showStats" class="flex justify-between">
        <!-- Stat total cycles -->
        <StatComponent
          v-if="meteringPoint.type === ASSET_TYPE.BATTERY"
          label="Totaal cycli periode"
          :value="totalBatteryCycles"
          :precision="2"
          class="flex-1"
        >
          <template #icon>
            <BaseIcon name="battery" filled class="fill-primary" />
          </template>
          <template #description="{ label }">
            {{ label }}
            <WikiTooltipComponent
              path="/nl/platform/measurements/battery-cycles"
              class="top-0.5 inline-block"
            />
          </template>
        </StatComponent>
        <!-- Stat avg cycles -->
        <StatComponent
          v-if="meteringPoint.type === ASSET_TYPE.BATTERY"
          label="Gem. cycli per dag"
          :value="avgBatteryCycles"
          :precision="2"
          class="flex-1"
        >
          <template #icon>
            <BaseIcon name="refresh" filled class="fill-primary" />
          </template>
          <template #description="{ label }">
            {{ label }}
            <WikiTooltipComponent
              path="/nl/platform/measurements/battery-cycles"
              class="top-0.5 inline-block"
            />
          </template>
        </StatComponent>
      </div>
      <!-- Chart -->
      <div :style="{ height: 350 + annotationData.length * 24 + 'px' }">
        <TimeseriesChart
          chart-type="area"
          :series="series"
          :axes="axes"
          :data="chartData"
          :annotation-data="annotationData.length ? annotationData : null"
          :x-limits="[dateRange[0], endOfDay(dateRange[1])]"
          :aligned-axes="false"
          :hidden-series="HIDDEN_SERIES"
        >
          <template #steering="{ label }">
            {{ label }}
            <WikiTooltipComponent
              path="/nl/platform/measurements/steering"
              class="top-0.5 inline-block"
            />
          </template>
          <template v-if="showAssetDetailsButton" #legend-message>
            <BaseButton
              small
              minimal
              color="primary"
              @click="onAssetDetailsClicked"
            >
              Installatie details
            </BaseButton>
          </template>
        </TimeseriesChart>
      </div>
    </LoadingWrapper>
  </MeteringPointCard>
</template>

<script setup>
import { ref, computed, watch } from "vue"
import { useRouter } from "vue-router"
import { differenceInCalendarDays, endOfDay } from "date-fns"
import { debounce, DEBOUNCE_DELAY } from "@/helpers/debounce.js"
import useNotificationStore from "@/stores/notificationStore.js"
import useFilterStore from "@/stores/filterStore.js"
import {
  BaseIcon,
  LoadingWrapper,
  TimeseriesChart,
  BaseButton,
} from "@repowerednl/ui-component-library"
import {
  ASSET,
  ASSET_TYPE,
} from "@/stores/repower/meteringPointStoreFactories.js"
import MeteringPointCard from "@/components/panels/MeteringPointCard.vue"
import StatComponent from "@/components/information/StatComponent.vue"
import WikiTooltipComponent from "@/components/information/WikiTooltipComponent.vue"

const props = defineProps({
  meteringPoint: {
    type: Object,
    default: null,
  },
  dateRange: {
    type: Array,
    required: true,
  },
  showStats: Boolean,
  showAnnotations: Boolean,
  showTitle: Boolean,
  showAssetDetailsButton: Boolean,
})

const DAYS_1MIN = 2
const DAYS_5MIN = 6
const DAYS_15MIN = 15
const DAYS_1H = 32

// Maps backend setpoint triggers to user-friendly labels for annotations
const TRIGGER_MAP = {
  OPTIMA: "Optima",
  CURTAILMENT: "Curtailment",
  CURTAILMENT_NET_ZERO: "Curtailment",
  IMBALANCE: "Onbalans",
  CBC: "CBC",
  AFRR_RESERVATION: "aFRR",
  NOMINATION_FOLLOWING: "Volg nominatie",
  SELF_CONSUMPTION: "Zelfconsumptie",
  AFRR_PREQUALIFICATION: "aFRR Prekwalificatie",
  ONBOARDING_TEST: "Test setpoint",
  API: "Demo setpoint",
  MANUAL: "Handmatig",
  DEFAULT: "Standaardstand",
}

const CBC_TRIGGER = "CBC"
const HIDDEN_SERIES = ["steering"]

const router = useRouter()
const filterStore = useFilterStore()
const notificationStore = useNotificationStore()
const chartData = ref({})
const loading = ref(false)
const loadingFailed = ref(false)
const totalBatteryCycles = ref()
const avgBatteryCycles = ref()
const annotationData = ref([])

const axes = {
  energy: {
    title: "Vermogen",
    unit: "W",
    suggestedMin: -10000, // -10 kW
    suggestedMax: 10000, // 10 kW
    minPrefix: "k",
  },
  percentage: {
    title: "State of charge",
    format: "_%",
    limits: [0, 100],
  },
}

const series = computed(() => {
  const basis = {
    soc: {
      label: "State of charge",
      axis: "percentage",
      rescale: 100,
      color: "secondary",
      type: "line",
    },
    power: {
      label: "Vermogen",
      axis: "energy",
      rescale: 1000,
      color: "energy",
    },
  }
  if (props.meteringPoint.type !== ASSET_TYPE.BATTERY) {
    delete basis.soc
  }
  return basis
})

/**
 * Optimise the frequency of the measurements for the selected date range.
 */
const freq = computed(() => {
  const days =
    1 + differenceInCalendarDays(props.dateRange[1], props.dateRange[0])
  if (days < DAYS_1MIN) {
    return "1min"
  } else if (days < DAYS_5MIN) {
    return "5min"
  } else if (days < DAYS_15MIN) {
    return "15min"
  } else if (days < DAYS_1H) {
    return "1h"
  } else {
    return "1D"
  }
})

/**
 * Creates and pushes a new annotation series.
 *
 * @param {string} key - Unique key for the annotation series.
 * @param {Array<Object>} data - Response data containing time windows.
 * @param {Object} options - Configuration options.
 * @param {string} options.label - The label of the annotation.
 * @param {string} options.color - Color name for the annotation.
 * @param {string} options.valueKey - Key name in 'data' entries to use as
 * series value.
 * @param {string|null} options.axis - Create a 'floating annotation' on this
 * axis.
 * @param {Function|null} options.descriptionCallback - A callback to generate
 * the description.
 * @param {Function|null} options.colorCallback - A callback to generate custom
 * color per entry.
 * @param {string} options.unit - Unit for the annotation values (default "W").
 */
function addAnnotations(key, data, options) {
  if (!data.length) {
    return
  }

  const {
    label,
    color,
    valueKey,
    axis = null,
    unit = "W",
    minPrefix = "k",
    // Per-entry callbacks, some override the default values above
    descriptionCallback = null,
    colorCallback = null,
    unitCallback = null,
  } = options

  // Prepare data input
  const preparedData = data.map((entry) => ({
    datetimeStart: entry.datetime_start,
    datetimeStop: entry.datetime_stop,
    value: entry[valueKey] * 1000,
    description: descriptionCallback?.(entry),
    color: colorCallback?.(entry),
    unit: unitCallback?.(entry),
  }))

  // Update or create annotation series for this key
  const annotation = annotationData.value.find((a) => a.key === key)
  if (annotation) {
    annotation.data = annotation.data.concat(preparedData)
  } else {
    annotationData.value.push({
      key,
      label,
      color,
      axis,
      unit,
      minPrefix,
      precision: 0,
      data: preparedData,
    })
  }
}

function onAssetDetailsClicked() {
  if (props.meteringPoint) {
    filterStore.setMeteringPoint(props.meteringPoint)
    router.push({ name: "assets-information" })
  }
}

function onNewMeasurements(data) {
  chartData.value = data
  loading.value = false
}

function onLoadMeasurementsFailed(error) {
  loadingFailed.value = true
  notificationStore.pushError(
    "Fout bij het ophalen van metingen",
    `De metingen van een installatie konden niet worden opgehaald. Probeer het later opnieuw. (code: ${error.code})`,
    "load-metering-point-data-error",
  )
}

function onNewBatteryCycles(data) {
  totalBatteryCycles.value = data.total
  avgBatteryCycles.value = data.avg
}

function onLoadBatteryCyclesFailed(error) {
  notificationStore.pushError(
    "Fout bij het ophalen van batterijcycli",
    `De batterijcycli van een installatie konden niet worden opgehaald. Probeer het later opnieuw. (code: ${error.code})`,
    "load-metering-point-data-error",
  )
}

function onNewFcrAllocations(data) {
  addAnnotations("balancing", data, {
    label: "Balanceringsdiensten",
    color: "finance",
    valueKey: "capacity_kw",
    descriptionCallback: () => "FCR",
  })
}

function onNewAfrrActivations(data) {
  const mappedData = data.map((entry) => ({
    ...entry,
    activated_value: entry.activated_up ?? entry.activated_down,
  }))
  addAnnotations("balancing", mappedData, {
    label: "Balanceringsdiensten",
    color: "finance",
    valueKey: "activated_value",
    descriptionCallback: (entry) =>
      entry.activated_up != null ? "aFRR (omhoog)" : "aFRR (omlaag)",
    unitCallback: () => "Wh",
  })
}

function onNewSetpoints(data) {
  addAnnotations("steering", data, {
    label: "Sturing",
    color: "primary",
    valueKey: "value",
    axis: "energy",
    descriptionCallback: (entry) => TRIGGER_MAP[entry.trigger],
    colorCallback: (entry) =>
      entry.trigger === CBC_TRIGGER ? "secondary" : undefined,
  })
}

function onLoadAnnotationsFailed(error) {
  notificationStore.pushError(
    "Fout bij het ophalen van annotaties",
    `Annotatiedata van een installatie kon niet worden opgehaald. Probeer het later opnieuw. (code: ${error.code})`,
    "load-metering-point-data-error",
  )
}

/**
 * Load measurements, annotations and battery KPIs for the current selection.
 */
const loadData = debounce((meteringPoint, dateRange) => {
  meteringPoint.loadMeasurements(
    freq.value,
    dateRange,
    false,
    onNewMeasurements,
    onLoadMeasurementsFailed,
  )
  // Load setpoints
  if (props.showAnnotations) {
    meteringPoint.loadSetpoints(
      dateRange,
      onNewSetpoints,
      onLoadAnnotationsFailed,
    )
  }
  // Load aFRR activations
  if (props.showAnnotations) {
    meteringPoint.loadAfrrActivations(
      dateRange,
      onNewAfrrActivations,
      onLoadAnnotationsFailed,
    )
  }

  if (meteringPoint.type === ASSET_TYPE.BATTERY) {
    // Load battery cycles
    if (props.showStats) {
      meteringPoint.loadBatteryCycles(
        dateRange,
        onNewBatteryCycles,
        onLoadBatteryCyclesFailed,
      )
    }
    // Load FCR allocations
    if (props.showAnnotations) {
      meteringPoint.loadFcrAllocations(
        dateRange,
        onNewFcrAllocations,
        onLoadAnnotationsFailed,
      )
    }
  }
}, DEBOUNCE_DELAY)

/**
 * Reset the chart and reload data when the inputs change.
 */
watch(
  [() => props.meteringPoint, () => props.dateRange],
  ([meteringPoint, dateRange]) => {
    if (meteringPoint?.role !== ASSET) {
      return
    }
    loading.value = true
    loadingFailed.value = false
    chartData.value = {}
    annotationData.value = []
    loadData(meteringPoint, dateRange)
  },
  { immediate: true },
)
</script>
